import { useState } from 'react';
import { Button, DatePicker, Descriptions, Input, Select, Space, Table, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import useFinanceTable from '../../../hooks/useFinanceTable';
import { authDetail, withdrawDetail, withdrawExport, withdrawList } from '../../../api/finance';
import FinanceExportButton from '../../../components/finance/FinanceExportButton';
import FinanceDetailModal from '../../../components/finance/FinanceDetailModal';
import FinanceStatusTag from '../../../components/finance/FinanceStatusTag';
import { can } from '../../../util/permission';
import { dayEnd, dayStart, formatDateTime, formatMoney } from '../../../util/finance';

export default function WithdrawTab() {
  const table = useFinanceTable(withdrawList);
  const [form, setForm] = useState({
    status: undefined,
    outBillNo: '',
    transferBillNo: '',
    range: null,
    userId: '',
  });
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [auth, setAuth] = useState(null);

  const search = () => {
    table.search({
      status: form.status,
      outBillNo: form.outBillNo || undefined,
      transferBillNo: form.transferBillNo || undefined,
      startTime: form.range ? dayStart(form.range[0]) : undefined,
      endTime: form.range ? dayEnd(form.range[1]) : undefined,
      userId: form.userId || undefined,
    });
  };

  const reset = () => {
    setForm({ status: undefined, outBillNo: '', transferBillNo: '', range: null, userId: '' });
    table.reset();
  };

  const openDetail = async (record) => {
    try {
      const res = await withdrawDetail(record.id);
      setDetail(res);
      setAuth(null);
      setDetailOpen(true);
    } catch (error) {
      message.error(error?.message || '查询详情失败');
    }
  };

  const showAuth = async () => {
    if (!detail?.signRecordId) {
      message.warning('该笔提现未关联授权书（历史数据）');
      return;
    }
    try {
      setAuth(await authDetail(detail.signRecordId));
    } catch (error) {
      message.error(error?.message || '查询授权书失败');
    }
  };

  const columns = [
    { title: '提现单号', dataIndex: 'outBillNo', width: 260, fixed: 'left', ellipsis: true },
    { title: '用户ID', dataIndex: 'userId', width: 100 },
    { title: '姓名', dataIndex: 'realName', width: 100 },
    { title: '提现金额', dataIndex: 'outMoney', width: 120, render: formatMoney },
    { title: '提现渠道', dataIndex: 'outChannel', width: 110 },
    { title: '微信流水号', dataIndex: 'transferBillNo', width: 220, ellipsis: true },
    {
      title: '状态',
      dataIndex: 'statusText',
      width: 90,
      render: (text) => <FinanceStatusTag text={text} />,
    },
    { title: '失败原因', dataIndex: 'failReason', width: 200, ellipsis: true },
    { title: '申请时间', dataIndex: 'createTime', width: 170, render: formatDateTime },
    { title: '到账时间', dataIndex: 'arrivalTime', width: 170, render: formatDateTime },
    {
      title: '授权书版本',
      dataIndex: 'docVersionNo',
      width: 130,
      render: (text) => text || '未关联（历史数据）',
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, record) =>
        can('finance:withdraw:detail') ? (
          <Button type="link" onClick={() => openDetail(record)}>
            详情
          </Button>
        ) : null,
    },
  ];

  return (
    <div>
      <div className="finance-toolbar">
        <Select
          value={form.status}
          onChange={(value) => setForm({ ...form, status: value })}
          placeholder="提现状态"
          allowClear
          style={{ width: 120 }}
          options={[
            { value: 'PROCESSING', label: '处理中' },
            { value: 'SUCCESS', label: '成功' },
            { value: 'FAIL', label: '失败' },
          ]}
        />
        <Input
          value={form.outBillNo}
          onChange={(e) => setForm({ ...form, outBillNo: e.target.value })}
          placeholder="提现单号"
          style={{ width: 220 }}
          allowClear
        />
        <Input
          value={form.transferBillNo}
          onChange={(e) => setForm({ ...form, transferBillNo: e.target.value })}
          placeholder="微信流水号"
          style={{ width: 200 }}
          allowClear
        />
        <DatePicker.RangePicker
          showTime
          value={form.range}
          onChange={(value) => setForm({ ...form, range: value })}
          style={{ width: 360 }}
        />
        <Input
          value={form.userId}
          onChange={(e) => setForm({ ...form, userId: e.target.value.replace(/\D/g, '') })}
          placeholder="用户ID"
          style={{ width: 110 }}
          allowClear
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={search}>
          查询
        </Button>
        <Button onClick={reset}>重置</Button>
        {can('finance:withdraw:export') && (
          <FinanceExportButton exportApi={withdrawExport} query={table.query} />
        )}
      </div>
      <Table
        className="finance-table"
        columns={columns}
        dataSource={table.data}
        rowKey="id"
        loading={table.loading}
        scroll={{ x: 1900 }}
        rowClassName={(_, index) => (index % 2 === 1 ? 'finance-row-odd' : '')}
        pagination={{
          current: table.pagination.current,
          pageSize: table.pagination.pageSize,
          total: table.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条数据`,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: table.onTableChange,
        }}
      />
      <FinanceDetailModal open={detailOpen} title="提现详情" onClose={() => setDetailOpen(false)}>
        {detail && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="提现单号">{detail.outBillNo}</Descriptions.Item>
              <Descriptions.Item label="微信流水号">{detail.transferBillNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="用户ID">{detail.userId}</Descriptions.Item>
              <Descriptions.Item label="姓名">{detail.realName || '-'}</Descriptions.Item>
              <Descriptions.Item label="提现金额">{formatMoney(detail.outMoney)}</Descriptions.Item>
              <Descriptions.Item label="提现渠道">{detail.outChannel}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <FinanceStatusTag text={detail.statusText} />
              </Descriptions.Item>
              <Descriptions.Item label="授权书版本">
                {detail.docVersionNo || '未关联（历史数据）'}
              </Descriptions.Item>
              <Descriptions.Item label="申请时间">{formatDateTime(detail.createTime)}</Descriptions.Item>
              <Descriptions.Item label="到账时间">{formatDateTime(detail.arrivalTime)}</Descriptions.Item>
              <Descriptions.Item label="失败原因" span={2}>
                {detail.failReason || '-'}
              </Descriptions.Item>
            </Descriptions>
            <Space style={{ marginTop: 12 }}>
              {can('finance:auth:detail') && (
                <Button onClick={showAuth} disabled={!detail.signRecordId}>
                  查看授权书存证
                </Button>
              )}
            </Space>
            {auth && (
              <div style={{ marginTop: 12 }}>
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="授权书版本">{auth.docVersionNo || '-'}</Descriptions.Item>
                  <Descriptions.Item label="签署时间">{formatDateTime(auth.signTime)}</Descriptions.Item>
                  <Descriptions.Item label="姓名">{auth.realName || '-'}</Descriptions.Item>
                  <Descriptions.Item label="身份证号">{auth.idCardNo || '-'}</Descriptions.Item>
                  <Descriptions.Item label="证据哈希" span={2}>
                    {auth.evidenceHash || '-'}
                  </Descriptions.Item>
                </Descriptions>
                <pre className="finance-pre">{auth.docContent || '授权书正文为空'}</pre>
              </div>
            )}
          </>
        )}
      </FinanceDetailModal>
    </div>
  );
}
